import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "ar_pwa_dismissed";

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}
function isMobile() {
  return /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent);
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) || !isMobile()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari no dispara beforeinstallprompt: mostramos una pista discreta.
    let t;
    if (isIOS()) {
      t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 1500);
    }

    const onInstalled = () => dismiss();
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (e) {
      console.error("PWA install: userChoice falló", e);
    }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-[60] lg:hidden"
      data-testid="pwa-install-banner"
    >
      <div className="mx-auto max-w-md rounded-xl bg-[#0E2A47] text-white shadow-2xl border border-white/10 px-4 py-3 flex items-center gap-3">
        <div className="shrink-0 h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center">
          <img src="/icons/icon-192.png" alt="AuditReady" className="h-8 w-8 rounded-md" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold leading-tight">
            Añadir AuditReady<span className="text-[#FF5625]">.</span> a tu pantalla de inicio
          </div>
          {iosHint ? (
            <div className="text-[11px] text-white/70 mt-0.5 flex items-center gap-1">
              Pulsa <Share size={12} className="inline" /> y luego “Añadir a pantalla de inicio”.
            </div>
          ) : (
            <div className="text-[11px] text-white/60 mt-0.5">Acceso directo, como una app.</div>
          )}
        </div>
        {!iosHint && (
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="shrink-0 inline-flex items-center gap-1 rounded-lg bg-[#FF5625] hover:bg-[#E0451A] transition-colors px-3 py-2 text-xs font-semibold"
          >
            <Download size={14} /> Instalar
          </button>
        )}
        <button
          onClick={dismiss}
          data-testid="pwa-dismiss-btn"
          aria-label="Descartar"
          className="shrink-0 text-white/60 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
